import InputWithSuggestions from "@/components/podkit/forms/InputWithSuggestions";
import { Label } from "@/components/podkit/forms/Label";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useListContextUrls } from "@/hooks/use-context-url";
import { TrackLocations } from "@/hooks/use-segment";
import { isNotFoundError, isUnauthorizedError } from "@/queries/errors";
import { useParseContextURL } from "@/queries/runner-queries";
import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from "react";
import { useDebounce } from "use-debounce";

export type ParsedGitInfo = {
    cloneUrl: string;
    branch?: string;
    commit?: string;
    repo?: string;
    upstreamRemoteUrl?: string;
};

/**
 * Result object returned by RepositoryInput when using result-based onChange
 */
export type RepositoryInputResult = {
    /** Whether the URL is valid */
    isValid: boolean;
    /** The original URL entered by the user */
    originalUrl: string;

    /** Parsed clone URL (e.g., https://github.com/user/repo.git) */
    cloneUrl?: string;
    /** Resolved branch name from PR/MR or direct branch reference */
    branch?: string;
    /** Specific commit SHA if applicable */
    commit?: string;
    /** Full parsed repository information */
    parsedInfo?: ParsedGitInfo;
    /** Whether there was a parsing error (e.g., PR not found, unauthorized) */
    hasParsingError?: boolean;
};

type Props = {
    /** Input label text */
    label?: string;
    /** Current input value */
    value?: string;
    /** HTML input name attribute */
    name?: string;
    /** Whether the input is disabled */
    disabled?: boolean;
    /** External error message to display */
    errorMessage?: string;
    /** Whether to auto-focus the input */
    autoFocus?: boolean;
    /** Runner ID for backend parsing (enables advanced URL parsing) */
    runnerId?: string;
    /** Whether to reserve space for error message when no error is present */
    reserveErrorSpace?: boolean;

    /**
     * Change handler - supports two modes:
     * - Result-based: (result: RepositoryInputResult) => void
     * - String-based: (value: string) => void (when using specialized callbacks)
     */
    onChange?: ((result: RepositoryInputResult) => void) | ((value: string) => void);

    // Specialized callback props for specific use cases
    /** Called when URL validation state changes */
    onValidationChange?: (isValid: boolean) => void;
};

/**
 * RepositoryInput - A smart input component for repository URLs
 *
 * Features:
 * - URL format validation
 * - Advanced parsing of PR/MR URLs to clone URLs (when runnerId provided)
 * - Two API styles: result-based onChange or specialized callbacks
 *
 * Usage:
 * ```tsx
 * // Result-based
 * <RepositoryInput
 *   onChange={(result) => console.log(result.cloneUrl)}
 * />
 *
 * // Callback-based
 * <RepositoryInput
 *   onValidationChange={setIsValid}
 * />
 * ```
 */
export const RepositoryInput = forwardRef<HTMLInputElement, Props & PropsWithClassName>(
    (
        {
            label = "Repository URL",
            value = "",
            onChange,
            onValidationChange,
            name,
            disabled,
            className,
            errorMessage: externalErrorMessage,
            autoFocus,
            runnerId,
            reserveErrorSpace = true, // Default to true for backward compatibility
        },
        ref,
    ) => {
        const { contextUrls } = useListContextUrls();
        const elId = useId();
        const inputRef = useRef<HTMLInputElement>(null);

        // Forward ref to allow parent components to focus the input
        useImperativeHandle(
            ref,
            () =>
                ({
                    focus: () => inputRef.current?.focus(),
                    blur: () => inputRef.current?.blur(),
                }) as HTMLInputElement,
        );

        const [inputValue, setInputValue] = useState(value);

        // Initialize isValidURL based on the initial value instead of defaulting to false
        const [isValidURL, setIsValidURL] = useState(() => {
            if (!value) return false;
            try {
                return value.trim() !== "" && new URL(value.toLowerCase()).pathname.length > 3;
            } catch {
                return false;
            }
        });

        // Update internal state when external value changes
        useEffect(() => {
            setInputValue(value);
        }, [value]);

        // Debounce the input to prevent too many API calls while typing
        const [debouncedInput] = useDebounce(inputValue, 500);

        // Parse when runnerId is provided and URL is valid (both create and settings modes)
        // Don't parse invalid URLs as they will cause the query to hang
        const shouldParse = !!runnerId && !!debouncedInput && isValidURL;

        // Parse the context URL using the runner bridge when runnerId is available
        const {
            data: parsedContext,
            isLoading: isLoadingParseContext,
            error: parseError,
        } = useParseContextURL(runnerId, debouncedInput, {
            enabled: shouldParse,
        });

        // Handle input changes
        const handleChange = useCallback((newValue: string) => {
            const trimmedValue = newValue.trim();
            setInputValue(trimmedValue);
        }, []);

        // Detect if onChange expects a string (callback-based) or RepositoryInputResult (result-based)
        // Callback-based mode is when any of the specialized callbacks are provided
        const isCallbackBasedMode = !!onValidationChange;

        // Handle input changes and call appropriate onChange
        useEffect(() => {
            if (!onChange) return;

            if (isCallbackBasedMode) {
                // Callback-based mode: just pass the string value
                (onChange as (value: string) => void)(inputValue);
            } else {
                // Result-based mode: pass RepositoryInputResult with parsed data
                const result: RepositoryInputResult = {
                    isValid: isValidURL,
                    originalUrl: inputValue,
                    hasParsingError: !!parseError,
                };

                // Add parsed data if available (regardless of mode)
                if (parsedContext?.git) {
                    result.cloneUrl = parsedContext.git.cloneUrl;
                    result.branch = parsedContext.git.branch;
                    result.commit = parsedContext.git.commit;
                    result.parsedInfo = {
                        cloneUrl: parsedContext.git.cloneUrl,
                        branch: parsedContext.git.branch,
                        commit: parsedContext.git.commit,
                        repo: parsedContext.git.repo,
                        upstreamRemoteUrl: parsedContext.git.upstreamRemoteUrl,
                    };
                } else if (isValidURL && !isLoadingParseContext && !parseError) {
                    // Fallback: use original URL as clone URL (only if no parsing error)
                    result.cloneUrl = inputValue;
                    result.branch = undefined; // No branch info available
                }

                (onChange as (result: RepositoryInputResult) => void)(result);
            }
        }, [inputValue, isValidURL, parsedContext, isLoadingParseContext, parseError, onChange, isCallbackBasedMode]);

        // Validate URL format - runs AFTER onChange to ensure parent receives value first
        useEffect(() => {
            try {
                const valid = inputValue.trim() !== "" && new URL(inputValue.toLowerCase()).pathname.length > 3;
                setIsValidURL(valid);
                onValidationChange?.(valid);
            } catch {
                setIsValidURL(false);
                onValidationChange?.(false);
            }
        }, [inputValue, onValidationChange]);

        // Determine error message
        // Don't show errors for authentication or not-found cases - parent component handles these
        const errorMessage =
            externalErrorMessage ||
            (inputValue && !isValidURL ? "The URL must point to a Git repository." : "") ||
            (parseError && !isUnauthorizedError(parseError) && !isNotFoundError(parseError)
                ? "Could not parse repository URL"
                : "");

        return (
            <div className={cn("flex flex-col gap-2", className)}>
                <div className="flex w-full flex-col gap-1">
                    {!!label && (
                        <Label htmlFor={elId} className="font-medium">
                            {label}
                        </Label>
                    )}
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <InputWithSuggestions
                                ref={inputRef}
                                id={elId}
                                data-testid="context-url-input"
                                name={name}
                                value={inputValue}
                                disabled={disabled}
                                suggestions={contextUrls || []}
                                placeholder="https://github.com/microsoft/vscode-remote-try-go"
                                onChange={handleChange}
                                error={errorMessage}
                                reserveErrorSpace={reserveErrorSpace}
                                autoFocus={autoFocus}
                                data-track-location={TrackLocations.ContextUrlInput}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

RepositoryInput.displayName = "RepositoryInput";
